import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Ticket, Minus, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useHolidays } from '../../hooks/useQueries';

const todayMidnight = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Mirrors backend `is_package_usable` (routes/day_pass_packages.py) closely enough
// for UI purposes — the server re-validates authoritatively on submit.
const isPackageUsable = (pkg, now) => {
  if (pkg.status !== 'active') return false;
  if ((pkg.quantity_remaining || 0) <= 0) return false;
  // Sale-reward packages (pkg.offer) can be booked ahead for dates in their window.
  if (pkg.start_date && new Date(pkg.start_date) > now && !pkg.offer) return false;
  if (pkg.expiry_date && new Date(pkg.expiry_date) < now) return false;
  return true;
};

// One or two clicks to book with an already-owned Bulk Day Pass package:
// centre is auto-selected when the client only has passes for one venue,
// otherwise picked first; then a date and quantity, then Confirm. No time
// pickers (fixed 9am-8pm) and no payment step, since the package covers it.
export const QuickBookDayPassModal = ({ open, onClose, api, user, venues = [], dayPassBalance, onBooked }) => {
  const [dayPassBookables, setDayPassBookables] = useState([]);
  const [loadingBookables, setLoadingBookables] = useState(false);
  const [venueId, setVenueId] = useState('');
  const [date, setDate] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const { data: holidays = [] } = useHolidays();
  const holidayDateSet = useMemo(() => new Set(holidays.map((h) => h.date)), [holidays]);

  useEffect(() => {
    if (!open) {
      setVenueId(''); setDate(null); setQuantity(1);
      return;
    }
    setLoadingBookables(true);
    api.get('/bookables', { params: { bookable_type: 'day_pass' } })
      .then((res) => setDayPassBookables(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDayPassBookables([]))
      .finally(() => setLoadingBookables(false));
  }, [open, api]);

  // The actual current instant — matches backend `datetime.now(timezone.utc)`.
  // Using local midnight here (like the calendar's disabled-dates check below)
  // would wrongly mark a package whose start_date is UTC midnight "today" as
  // not-yet-started for any client in a timezone ahead of UTC.
  const usablePackages = useMemo(
    () => (dayPassBalance?.packages || []).filter((p) => isPackageUsable(p, new Date())),
    [dayPassBalance]
  );
  const unscopedRemaining = usablePackages
    .filter((p) => !p.venue_ids || p.venue_ids.length === 0)
    .reduce((s, p) => s + (p.quantity_remaining || 0), 0);
  const scopedVenueIds = useMemo(() => {
    const set = new Set();
    usablePackages.forEach((p) => (p.venue_ids || []).forEach((v) => set.add(v)));
    return set;
  }, [usablePackages]);

  const dayPassBookableVenueIds = useMemo(
    () => new Set(dayPassBookables.map((b) => b.venue_id)),
    [dayPassBookables]
  );

  // Packages with no venue_ids (legacy/unscoped) are usable at any venue that
  // actually has a Day Pass space — matches the backend drawdown fallback.
  const eligibleVenueIds = useMemo(() => {
    if (unscopedRemaining > 0) return Array.from(dayPassBookableVenueIds);
    return Array.from(scopedVenueIds).filter((v) => dayPassBookableVenueIds.has(v));
  }, [unscopedRemaining, scopedVenueIds, dayPassBookableVenueIds]);

  useEffect(() => {
    if (open && eligibleVenueIds.length === 1 && !venueId) {
      setVenueId(eligibleVenueIds[0]);
    }
  }, [open, eligibleVenueIds, venueId]);

  const remainingAtVenue = (vId) => {
    const scoped = usablePackages
      .filter((p) => (p.venue_ids || []).includes(vId))
      .reduce((s, p) => s + (p.quantity_remaining || 0), 0);
    return scoped + unscopedRemaining;
  };

  const venueName = (id) => venues.find((v) => v.id === id)?.name || id;
  const maxQuantity = venueId ? remainingAtVenue(venueId) : 0;

  const canBookSunday = ['admin', 'center_manager', 'cluster_manager'].includes(user?.role);
  const isDateDisabled = (d) => {
    const isSunday = d.getDay() === 0;
    return d < todayMidnight() || (isSunday && !canBookSunday) || holidayDateSet.has(format(d, 'yyyy-MM-dd'));
  };

  const submit = async () => {
    if (!venueId) { toast.error('Select a centre'); return; }
    if (!date) { toast.error('Select a date'); return; }
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    if (qty > maxQuantity) {
      toast.error(`Only ${maxQuantity} pass${maxQuantity !== 1 ? 'es' : ''} available at this centre`);
      return;
    }
    const bookable = dayPassBookables.find((b) => b.venue_id === venueId);
    if (!bookable) { toast.error('No Day Pass space configured at this centre'); return; }

    setSubmitting(true);
    try {
      await api.post('/bookings', {
        bookable_id: bookable.id,
        venue_id: venueId,
        booking_date: format(date, 'yyyy-MM-dd'),
        start_time: '09:00',
        end_time: '20:00',
        attendees: qty,
        use_credits: false,
        use_day_pass: true,
        is_internal: false,
        pass_quantity: qty,
        pass_days: 1,
      });
      toast.success(`Booked ${qty} Bulk Day Pass${qty !== 1 ? 'es' : ''} at ${venueName(venueId)}`);
      onBooked?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="quick-book-day-pass-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ticket className="w-5 h-5 text-primary" /> Book with Bulk Day Passes</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {loadingBookables ? (
            <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : eligibleVenueIds.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No Day Pass space is available at your allocated centre(s) right now.</p>
          ) : (
            <>
              {eligibleVenueIds.length > 1 && (
                <div className="space-y-2">
                  <Label>Centre *</Label>
                  <Select value={venueId} onValueChange={setVenueId}>
                    <SelectTrigger data-testid="quick-book-venue-select"><SelectValue placeholder="Select a centre" /></SelectTrigger>
                    <SelectContent>
                      {eligibleVenueIds.map((vId) => (
                        <SelectItem key={vId} value={vId}>{venueName(vId)} ({remainingAtVenue(vId)} passes)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {venueId && (
                <>
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={isDateDisabled}
                      className="rounded-md border-0"
                      data-testid="quick-book-calendar"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Number of Day Passes *</Label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        className="w-8 h-8 rounded-md border border-border flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-40"
                        disabled={quantity <= 1}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <Input
                        type="number" min={1} max={maxQuantity || 1} value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, Math.min(maxQuantity || 1, parseInt(e.target.value) || 1)))}
                        className="w-16 text-center" data-testid="quick-book-quantity-input"
                      />
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.min(maxQuantity || 1, q + 1))}
                        className="w-8 h-8 rounded-md border border-border flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-40"
                        disabled={quantity >= maxQuantity}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-xs text-muted-foreground">of {maxQuantity} available</span>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !venueId || !date} data-testid="quick-book-confirm-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ticket className="w-4 h-4 mr-2" />}
            Confirm Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickBookDayPassModal;
